import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    CreditCard,
    Calendar,
    CheckCircle,
    AlertCircle,
    Loader2,
    XCircle,
    RefreshCw,
    ArrowUpCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const MySubscription = () => {
    const [subscription, setSubscription] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                setError("You must be logged in to view subscription.");
                setLoading(false);
                return;
            }

            try {
                const subRes = await api.get("/subscriptions", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const plansRes = await api.get("/plans", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (subRes.data.success && subRes.data.data) {
                    setSubscription(subRes.data.data);
                } else {
                    setError("No active subscription found.");
                }

                if (plansRes.data.success && plansRes.data.data) {
                    setPlans(plansRes.data.data.filter(p => p.is_active));
                } else {
                    setPlans([]);
                }
            } catch (err) {
                const message = err.response?.data?.message || "Failed to load data.";
                setError(message);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: message,
                    confirmButtonColor: "#1e40af",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const getStatusBadge = (status, isTrial) => {
        const styles = {
            active: "bg-green-100 text-green-800 border-green-200",
            cancelled: "bg-red-100 text-red-800 border-red-200",
            trial: "bg-blue-100 text-blue-800 border-blue-200",
            default: "bg-gray-100 text-gray-800 border-gray-200",
        };

        const style = isTrial ? styles.trial : styles[status] || styles.default;
        const label = isTrial ? "Trial" : status.charAt(0).toUpperCase() + status.slice(1);
        const icon = isTrial || status === "active" ? (
            <CheckCircle className="w-4 h-4" />
        ) : (
            <AlertCircle className="w-4 h-4" />
        );

        return (
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${style}`}>
                {icon}
                {label}
            </span>
        );
    };

    // إلغاء الاشتراك
    const handleCancel = async () => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Cancel Subscription?",
            text: "Your access will continue until the end of the billing period.",
            showCancelButton: true,
            confirmButtonText: "Yes, Cancel",
            cancelButtonText: "Keep Active",
            confirmButtonColor: "#dc2626",
        });

        if (!result.isConfirmed) return;

        setActionLoading(true);
        const token = localStorage.getItem("auth_token");

        try {
            await api.post(
                `/subscriptions/${subscription.id}/cancel`,
                { immediately: false },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSubscription((prev) => ({ ...prev, status: "cancelled" }));

            Swal.fire({
                icon: "success",
                title: "Cancelled",
                text: "Your subscription will end at the current billing period.",
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed",
                text: err.response?.data?.message || "Could not cancel subscription.",
            });
        } finally {
            setActionLoading(false);
        }
    };

    // استئناف الاشتراك
    const handleResume = async () => {
        const result = await Swal.fire({
            icon: "question",
            title: "Resume Subscription?",
            text: "Your subscription will be reactivated immediately.",
            showCancelButton: true,
            confirmButtonText: "Yes, Resume",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#10b981",
        });

        if (!result.isConfirmed) return;

        setActionLoading(true);
        const token = localStorage.getItem("auth_token");

        try {
            await api.post(
                `/subscriptions/${subscription.id}/resume`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSubscription((prev) => ({ ...prev, status: "active" }));

            Swal.fire({
                icon: "success",
                title: "Resumed!",
                text: "Your subscription is active again.",
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed",
                text: err.response?.data?.message || "Could not resume subscription.",
            });
        } finally {
            setActionLoading(false);
        }
    };

    // تبديل الخطة
    const handleSwap = async () => {
        if (!selectedPlanId) {
            Swal.fire("Error", "Please select a plan.", "warning");
            return;
        }

        const plan = plans.find((p) => p.id === parseInt(selectedPlanId));
        const result = await Swal.fire({
            icon: "question",
            title: `Switch to ${plan.name}?`,
            html: `
                <p>Price: <strong>$${plan.price}/${plan.billing_interval}</strong></p>
                <p class="text-sm text-gray-600 mt-2">
                    Lawyers: <strong>${plan.max_lawyers === 0 ? "Unlimited" : plan.max_lawyers}</strong><br>
                    Forms/Month: <strong>${plan.max_forms_per_month === 0 ? "Unlimited" : plan.max_forms_per_month}</strong><br>
                    Paralegals/Lawyer: <strong>${plan.max_paralegals_per_lawyer === 0 ? "Unlimited" : plan.max_paralegals_per_lawyer}</strong>
                </p>
            `,
            showCancelButton: true,
            confirmButtonText: "Yes, Switch",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#1e40af",
        });

        if (!result.isConfirmed) return;

        setActionLoading(true);
        const token = localStorage.getItem("auth_token");

        try {
            const res = await api.post(
                `/subscriptions/${subscription.id}/swap`,
                { plan_id: parseInt(selectedPlanId) },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSubscription(res.data.data);
            setShowUpgradeModal(false);
            setSelectedPlanId("");

            Swal.fire({
                icon: "success",
                title: "Plan Changed!",
                text: `You are now on the ${plan.name} plan.`,
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed",
                text: err.response?.data?.message || "Could not change plan.",
            });
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error || !subscription) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <p className="text-red-700 font-medium">{error || "No subscription found."}</p>
            </div>
        );
    }

    const { plan, status, starts_at, ends_at, trial_ends_at, is_on_trial } = subscription;

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">My Subscription</h2>
                    <p className="text-gray-600 mt-1">View and manage your current plan details</p>
                </div>
                <div className="flex items-center gap-2">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">Active Plan</span>
                </div>
            </div>

            {/* Subscription Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-6">
                {/* Plan Header */}
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{plan.description || "No description"}</p>
                    </div>
                    <div className="flex flex-col gap-[10px] text-right">
                        <p className="text-3xl font-bold text-blue-600">
                            ${plan.price}
                            <span className="text-sm font-normal text-gray-600">/{plan.billing_interval}</span>
                        </p>
                        <div className="w-fit">
                            {getStatusBadge(status, is_on_trial)}
                        </div>
                    </div>
                </div>

                <div className="h-px bg-gray-200"></div>

                {/* Plan Limits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <p className="text-xs font-medium text-blue-700 uppercase">Lawyers</p>
                        <p className="text-2xl font-bold text-blue-900 mt-1">
                            {plan.max_lawyers === 0 ? "Unlimited" : plan.max_lawyers}
                        </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                        <p className="text-xs font-medium text-green-700 uppercase">Forms / Month</p>
                        <p className="text-2xl font-bold text-green-900 mt-1">
                            {plan.max_forms_per_month === 0 ? "Unlimited" : plan.max_forms_per_month}
                        </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                        <p className="text-xs font-medium text-purple-700 uppercase">Paralegals / Lawyer</p>
                        <p className="text-2xl font-bold text-purple-900 mt-1">
                            {plan.max_paralegals_per_lawyer === 0 ? "Unlimited" : plan.max_paralegals_per_lawyer}
                        </p>
                    </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-gray-500" />
                        <div>
                            <p className="font-medium text-gray-700">Started On</p>
                            <p className="text-gray-600">{formatDate(starts_at)}</p>
                        </div>
                    </div>
                    {is_on_trial && trial_ends_at && (
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-blue-500" />
                            <div>
                                <p className="font-medium text-gray-700">Trial Ends</p>
                                <p className="text-blue-600">{formatDate(trial_ends_at)}</p>
                            </div>
                        </div>
                    )}
                    {ends_at && (
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-red-500" />
                            <div>
                                <p className="font-medium text-gray-700">Ends On</p>
                                <p className="text-red-600">{formatDate(ends_at)}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-200">
                    {status === "active" && !is_on_trial && (
                        <motion.button
                            onClick={handleCancel}
                            disabled={actionLoading}
                            className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all flex items-center gap-2 disabled:opacity-70"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <XCircle className="w-4 h-4" />
                            {actionLoading ? "Processing..." : "Cancel Subscription"}
                        </motion.button>
                    )}

                    {status === "cancelled" && (
                        <motion.button
                            onClick={handleResume}
                            disabled={actionLoading}
                            className="px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all flex items-center gap-2 disabled:opacity-70"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <RefreshCw className="w-4 h-4" />
                            {actionLoading ? "Processing..." : "Resume"}
                        </motion.button>
                    )}

                    <motion.button
                        onClick={() => setShowUpgradeModal(true)}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <ArrowUpCircle className="w-4 h-4" />
                        Upgrade Plan
                    </motion.button>
                </div>
            </div>

            {/* Upgrade Modal */}
            {showUpgradeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
                    >
                        <h3 className="text-xl font-semibold text-gray-900 mb-5">Choose a New Plan</h3>

                        {plans.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No plans available.</p>
                        ) : (
                            <div className="space-y-3">
                                {plans
                                    .filter((p) => p.id !== plan.id)
                                    .map((p) => (
                                        <label
                                            key={p.id}
                                            className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${selectedPlanId === p.id.toString()
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-gray-200 hover:border-gray-300"
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="plan"
                                                    value={p.id}
                                                    checked={selectedPlanId === p.id.toString()}
                                                    onChange={(e) => setSelectedPlanId(e.target.value)}
                                                    className="w-4 h-4 text-blue-600"
                                                />
                                                <div>
                                                    <p className="font-semibold text-gray-900">{p.name}</p>
                                                    <p className="text-xs text-gray-600">{p.description}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-blue-600">
                                                    ${p.price}<span className="text-sm font-normal text-gray-600">/{p.billing_interval}</span>
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {p.max_lawyers === 0 ? "Unlimited" : p.max_lawyers} Lawyers
                                                </p>
                                            </div>
                                        </label>
                                    ))}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowUpgradeModal(false);
                                    setSelectedPlanId("");
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSwap}
                                disabled={actionLoading || !selectedPlanId}
                                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-70"
                            >
                                {actionLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Switching...
                                    </>
                                ) : (
                                    "Switch Plan"
                                )}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default MySubscription;